import { applyActionCode, Auth, browserLocalPersistence,
	browserSessionPersistence, confirmPasswordReset,
	createUserWithEmailAndPassword, getAuth, GoogleAuthProvider,
	inMemoryPersistence, sendEmailVerification, sendPasswordResetEmail,
	setPersistence, signInAnonymously, signInWithEmailAndPassword,
	signInWithPopup, signOut, User, verifyPasswordResetCode }
	from 'firebase/auth';

export class FbAuth {
	
	public static async signInAnonymously():Promise<User> {
		try {
			let auth = getAuth();
			let credential = await signInAnonymously(auth);
			return credential.user;
		}
		catch (err){
			throw err;
		}
	}

	public static isCurrentUserAnonymous():boolean {
		return getAuth().currentUser?.isAnonymous === true;
	}

	/**returns true if a un-anonymous user is signed in */
	public static isRealUserSignedIn():boolean {
		let user = getAuth().currentUser;
		return (user != null && !user.isAnonymous);
	}
	
	public static async createUserWithEmailAndPassword(email:string, 
			password:string):Promise<User> {
		try {
			let auth = getAuth();
			let credential = await createUserWithEmailAndPassword(auth,
				email, password);
			return credential.user;
		}
		catch (err) {
			throw err;
		}
	}
	
	public static emailVerified():boolean {
		return (FbAuth.getCurrentUser()?.emailVerified)?? false;
	}
	
	/**Attempts to send a verification email to the user. If the email is sent,
	 * returns [true]*/
	public static async sendEmailVerification(redirectUrl:string)
			:Promise<boolean> {
		let user = FbAuth.getCurrentUser();
		if (user != null){
			try {
				// let pageUrl = AppSettings.externalUrl;
				// await sendEmailVerification(user, {
				// 	url: `${pageUrl}?confirm_email=true`
				// });
				await sendEmailVerification(user, {
					url: redirectUrl,
					handleCodeInApp: false
				});
				return true;
			}
			catch (err){
				console.log(err);
				return false;
			}
		}
		return false;
	}
	
	public static async verifyEmail(oobCode:string):Promise<boolean> {
		try {
			let auth = getAuth();
			await applyActionCode(auth, oobCode);
			return true;
		}
		catch (err){
			console.log(err);
			return false;
		}
	}
	
	public static async canResetPassword(oobCode:string):Promise<boolean> {
		try {
			let auth = getAuth();
			await verifyPasswordResetCode(auth, oobCode);
			return true;
		}
		catch (err){
			return false;
		}
	}
	
	public static async updatePassword(oobCode:string, newPassword:string)
			:Promise<boolean> {
		try {
			let auth = getAuth();
			await confirmPasswordReset(auth, oobCode, newPassword);
			return true;
		}
		catch (err){
			console.log(err);
			return false;
		}
	}
	
	public static async signInWithEmailAndPassword(email:string, 
			password:string):Promise<User> {
		try {
			let auth = getAuth();
			let credential = await signInWithEmailAndPassword(auth,
				email, password);
			return credential.user;
		}
		catch (err){
			throw err;
		}
	}
	
	public static async sendPasswordResetEmail(email:string):Promise<void> {
		try {
			let auth = getAuth();
			let result = await sendPasswordResetEmail(auth, email);
			// console.log(result);
		}
		catch(err){
			throw err;
		}
	}
	
	/**Shows a Google login popup - returns a Promise that resolves to the
	 * logged in user, or null if login doesn't occur.*/
	public static async showLogin():Promise<User> {
		try {
			let provider = new GoogleAuthProvider()
			provider.setCustomParameters({ 'prompt': 'select_account'});
			let auth = getAuth();
			let result = await signInWithPopup(auth, provider);
			
			return result.user;
		}
		catch(err){
			throw err;
		}
	}
	
	/**Logs the user out. Returns a Promise that resolves when operation is
	 * complete.*/
	public static async logout():Promise<void> {
		try {
			let auth = getAuth();
			await signOut(auth);
		}
		catch (err){
			throw err;
		}
	}
	
	/**Sets the persistance of the user login. [persistance] matches a value
	 * from [fb.Persistence]. Info here:
	 * https://firebase.google.com/docs/auth/web/auth-state-persistence
	 * @param persistence a string that is LOCAL, SESSION, or NONE*/
	public static async setAuthPersistence(persistence:string = "LOCAL"
			):Promise<void> {
		try {
			let auth = getAuth();
			switch (persistence){
				case "LOCAL":
					await setPersistence(auth, browserLocalPersistence);
					break;
				case "SESSION":
					await setPersistence(auth, browserSessionPersistence);
					break;
				case "NONE":
					await setPersistence(auth, inMemoryPersistence);
					break;
				default:
					throw 'invalid persistence value: ' + persistence;
			}
		}
		catch(err){
			throw err;
		}
	}
	
	/**gets the currently signed in user*/
	public static getCurrentUser():User {
		return getAuth().currentUser;
	}
	
	public static async reloadUser():Promise<void> {
		let user = FbAuth.getCurrentUser();
		if (user != null){
			try {
				await user.reload();
			}
			catch (err){
				console.log(err);
			}
		}
	}
	
	/**gets the JWT auth token for the current user - returns null if user
	 * not logged in.*/
	public static async getUserToken(forceRefresh:boolean = false)
			:Promise<string> {
		try {
			let user = getAuth().currentUser;
			if (user != null){
				let token:string = await user.getIdToken(forceRefresh);
				return token;
			}
			return null;
		}
		catch(err) {
			throw err;
		}
	}
	
	public static get auth():Auth { return getAuth(); }
}